/**
 *
 * Goals:
 *  - handle incoming requests
 *  - propagate errorId to the user if available
 *
 * Note: Error logging is handled globally by react-query's onError handlers
 */
// common types for API communication
// it's duplicated in each client's code
import { ApiResponse, ErrorType } from '@ndvr/auth-api';

import { sanitizeBackendMessage } from './error-utils';
import { downloadFile } from './file-download';

const HTTP_CODE = {
    success: 200,
    created: 201,
    noContent: 204,
    badRequest: 400,
    unauthorized: 401,
    notFound: 404,
    serverError: 500,
};

export class FetcherError extends Error {
    readonly errorId?: string;
    readonly errorType?: ErrorType | 'NETWORK_ERROR' | 'EDGE_REJECTED';
    readonly status?: number;
    readonly url?: string;
    readonly exception?: unknown;
    readonly hideError: boolean;
    readonly httpStatus?: number;
    /** Raw backend-provided message. For telemetry and control flow only — never render to users; use `.message`. */
    readonly backendMessage?: string;

    constructor(props: {
        message?: string;
        errorId?: string;
        errorType?: ErrorType | 'NETWORK_ERROR' | 'EDGE_REJECTED';
        status?: number;
        url?: string;
        exception?: unknown;
        hideError?: boolean;
        httpStatus?: number;
        backendMessage?: string;
    }) {
        super(props.message);

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, FetcherError);
        }

        this.name = 'FetcherError';
        this.errorId = props.errorId;
        this.errorType = props.errorType;
        this.status = props.status;
        this.url = props.url;
        this.exception = props.exception;
        this.hideError = props.hideError || false;
        this.httpStatus = props.httpStatus;
        this.backendMessage = props.backendMessage;
    }
}

const createError = (errorInfo: {
    message?: string;
    errorId?: string;
    errorType?: ErrorType | 'NETWORK_ERROR' | 'EDGE_REJECTED';
    status?: number;
    url?: string;
    exception?: unknown;
    hideError?: boolean;
    httpStatus?: number;
    backendMessage?: string;
}) => new FetcherError(errorInfo);

/**
 * Calling `grabPayload` can be done once for a given response as the response stream
 * is not processable multiple times.
 */
const grabPayload = async <T>(response: Response): Promise<ApiResponse<T>> => {
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition && response.body) {
        const fileName = contentDisposition.split('filename=')[1].replace(/['"]/g, '');
        fileName && downloadFile(fileName, window.URL.createObjectURL(await response.blob()));
        return [null, null];
    }

    const contentType = response.headers.get('Content-Type');
    // no content type must mean no content ¯\_(ツ)_/¯
    if (contentType === null) {
        return [null, null];
    }
    // we only accept JSON data
    if (contentType.includes('application/json')) {
        const payload = await response.json();
        return [payload as T, null];
    }

    throw new FetcherError({ message: 'Unprocessable entity' });
};

export async function fetcher<T>(url: string, config: RequestInit, hideError = true): Promise<ApiResponse<T>> {
    let response: Response;
    try {
        response = await fetch(url, config);
    } catch (ex: any) {
        throw createError({ message: 'Network Error', errorType: 'NETWORK_ERROR', exception: ex, url, hideError });
    }

    if (response.status === HTTP_CODE.noContent) {
        return [null, null];
    }

    let payload;
    try {
        payload = await grabPayload<T>(response);
    } catch (ex: any) {
        throw createError({
            message: 'Failed to process response',
            status: response.status,
            exception: ex,
            url,
            hideError,
        });
    }

    // 4xx or 5xx status code
    if (response.status.toString().startsWith('4') || response.status.toString().startsWith('5')) {
        const errorResponse = (payload[0] || { errorType: 'unknown error' }) as {
            errorId?: string;
            errorType?: ErrorType;
            message?: string;
        };

        // Edge/WAF rejection: no backend ran. Discriminator is the ALB
        // `Server: awselb/...` response header; the body is empty and has no errorId.
        const isEdgeRejection = !!response.headers.get('server')?.startsWith('awselb/');

        if (isEdgeRejection) {
            throw createError({
                message:
                    'Try saving a fresh copy of the file (for example, Print → Save as PDF) and upload the new copy. If it still does not work, please contact support.',
                errorType: 'EDGE_REJECTED',
                status: response.status,
                url,
                hideError,
                httpStatus: response.status,
            });
        }

        throw createError({
            message: sanitizeBackendMessage(errorResponse.message, errorResponse.errorType),
            backendMessage: errorResponse.message,
            errorType: errorResponse.errorType,
            errorId: errorResponse.errorId,
            status: response.status,
            url,
            hideError,
            httpStatus: response.status,
        });
    }

    return payload;
}
